import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Scenographic intro — "entering a luxury hotel".
 * Sequence:
 *   0.0 – 0.5s   dark closed doors, subtle grain
 *   0.5 – 1.2s   a warm brass light appears at the seam (a hotel door opening slowly)
 *   1.2 – 2.6s   two doors slide apart revealing an ivory interior
 *   1.8 – 3.2s   ZITRO logo emerges with staggered accent dot + brass hairline + kicker
 *   3.4 – 4.4s   whole overlay slides upward, revealing the site
 */
export const IntroLoader = () => {
  const [visible, setVisible] = useState(true);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    // Disable browser's automatic scroll restoration and force top position
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    document.body.style.overflow = "hidden";
    const t1 = setTimeout(() => setStage(1), 500);   // seam light
    const t2 = setTimeout(() => setStage(2), 1200);  // doors open
    const t3 = setTimeout(() => setStage(3), 2300);  // logo reveal (+0.4s later opening)
    const t4 = setTimeout(() => setVisible(false), 4200); // exit — +0.4s linger on logo
    return () => {
      [t1, t2, t3, t4].forEach(clearTimeout);
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (!visible) {
      document.body.style.overflow = "";
      // Ensure we land at the top of the page once the intro is gone
      window.scrollTo(0, 0);
    }
  }, [visible]);

  const seamOn = stage >= 1;
  const doorsOpen = stage >= 2;
  const showLogo = stage >= 3;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[100] overflow-hidden intro-cover"
          exit={{ y: "-100%" }}
          transition={{ duration: 1.05, ease: [0.85, 0, 0.15, 1] }}
          data-testid="intro-loader"
        >
          {/* IVORY INTERIOR — visible after the doors slide apart */}
          <div className="absolute inset-0 bg-[#F1EEE8]">
            {/* subtle vignette + grain to feel like a real lobby */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(28,26,23,0.00) 55%, rgba(28,26,23,0.10) 100%)",
              }}
            />
            {/* Logo block — centered, revealed once doors are open */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showLogo ? 1 : 0 }}
              transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6 text-center"
            >
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: showLogo ? 1 : 0, y: showLogo ? 0 : 8 }}
                transition={{ delay: 0.15, duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
                className="text-[0.6rem] tracking-[0.55em] uppercase text-[#8A6F45]"
                style={{ fontFamily: "Inter", fontWeight: 500 }}
              >
                Benvenuti
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: showLogo ? 1 : 0 }}
                transition={{ delay: 0.25, duration: 1.15, ease: [0.7, 0, 0.2, 1] }}
                className="flex items-center justify-center"
              >
                <img
                  src="/brand/zitro-logo-dark.png"
                  alt="Zitro Property Management"
                  className="w-auto select-none"
                  draggable="false"
                  style={{ height: "clamp(120px, 22vw, 260px)" }}
                />
              </motion.div>

              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: showLogo ? 1 : 0 }}
                transition={{ delay: 0.7, duration: 0.85, ease: [0.7, 0, 0.2, 1] }}
                className="h-px bg-[#8A6F45]/70 origin-center"
                style={{ width: "min(200px, 44vw)" }}
              />

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: showLogo ? 1 : 0, y: showLogo ? 0 : 8 }}
                transition={{ delay: 1.05, duration: 0.9 }}
                className="text-[0.65rem] tracking-[0.4em] uppercase text-[#232326]/60"
                style={{ fontFamily: "Inter", fontWeight: 500 }}
              >
                Property Management &nbsp;·&nbsp; Lago di Como
              </motion.div>
            </motion.div>
          </div>

          {/* LEFT DOOR */}
          <motion.div
            initial={{ x: 0 }}
            animate={{ x: doorsOpen ? "-101%" : 0 }}
            transition={{ duration: 1.5, ease: [0.85, 0, 0.15, 1] }}
            className="absolute top-0 bottom-0 left-0 w-1/2 bg-[#0F0E0D]"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(28,26,23,0.15) 55%, rgba(138,111,69,0.10) 100%)",
              boxShadow: "inset -1px 0 0 rgba(138,111,69,0.15)",
            }}
          >
            {/* door frame detail: brass hairline near the seam */}
            <div className="absolute inset-y-10 right-4 md:right-8 w-px bg-[#8A6F45]/22" />
            {/* subtle grain */}
            <div
              className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(255,255,255,0.35) 0.5px, transparent 0.5px)",
                backgroundSize: "3px 3px",
              }}
            />
          </motion.div>

          {/* RIGHT DOOR */}
          <motion.div
            initial={{ x: 0 }}
            animate={{ x: doorsOpen ? "101%" : 0 }}
            transition={{ duration: 1.5, ease: [0.85, 0, 0.15, 1] }}
            className="absolute top-0 bottom-0 right-0 w-1/2 bg-[#0F0E0D]"
            style={{
              backgroundImage:
                "linear-gradient(-90deg, rgba(0,0,0,0.65) 0%, rgba(28,26,23,0.15) 55%, rgba(138,111,69,0.10) 100%)",
              boxShadow: "inset 1px 0 0 rgba(138,111,69,0.15)",
            }}
          >
            <div className="absolute inset-y-10 left-4 md:left-8 w-px bg-[#8A6F45]/22" />
            <div
              className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(255,255,255,0.35) 0.5px, transparent 0.5px)",
                backgroundSize: "3px 3px",
              }}
            />
          </motion.div>

          {/* SEAM LIGHT — the warm gap between the two doors before they open */}
          <motion.div
            initial={{ opacity: 0, scaleY: 0.2 }}
            animate={{
              opacity: seamOn && !doorsOpen ? 1 : 0,
              scaleY: seamOn ? 1 : 0.2,
            }}
            transition={{ duration: 0.75, ease: [0.7, 0, 0.2, 1] }}
            className="absolute top-0 bottom-0 left-1/2 pointer-events-none origin-center"
            style={{
              width: "3px",
              transform: "translateX(-1.5px)",
              background:
                "linear-gradient(to bottom, transparent 0%, rgba(214,179,124,0.85) 25%, rgba(214,179,124,1) 50%, rgba(214,179,124,0.85) 75%, transparent 100%)",
              boxShadow:
                "0 0 20px 6px rgba(214,179,124,0.55), 0 0 60px 12px rgba(214,179,124,0.28)",
              zIndex: 5,
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
